if (!customElements.get("wd-product-bundle")) {
  class WdProductBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector(".wd-pbundle__button");
      this.errorEl = this.querySelector(".wd-pbundle__error");
      this.productsEl = this.querySelector(".wd-pbundle__products");
      this.variantId = this.dataset.variantId;
      this.descriptions = (this.dataset.descriptions || "")
        .split("|||")
        .map((value) => value.trim());
      this.productCount = this.dataset.productCount || "3";
      this.moneyFormat = this.dataset.moneyFormat || "{{amount}}";
      this.hasNativeComponents = null;

      if (this.button && this.variantId) {
        this.button.addEventListener("click", this.onAdd.bind(this));
      }

      if (this.dataset.storefrontToken && this.dataset.bundleHandle) {
        this.syncNativeBundle();
      } else if (this.productsEl?.querySelector(".wd-pbundle__empty")) {
        this.clearLoading("לא נמצאו מוצרים בבאנדל");
      }
    }

    async syncNativeBundle() {
      const token = this.dataset.storefrontToken;
      const handle = this.dataset.bundleHandle;
      const shouldRender = Boolean(
        this.productsEl?.querySelector(".wd-pbundle__empty"),
      );

      if (!token || !handle) {
        if (shouldRender) {
          this.clearLoading("לא נמצאו מוצרים בבאנדל");
        }
        return;
      }

      try {
        const variantGid = this.variantId
          ? `gid://shopify/ProductVariant/${this.variantId}`
          : null;
        const query = `
          query BundleComponents($handle: String!, $variantId: ID) {
            product(handle: $handle) {
              variant(id: $variantId) {
                id
                components(first: 10) {
                  nodes {
                    quantity
                    productVariant {
                      price { amount }
                      compareAtPrice { amount }
                      image { url altText }
                      product {
                        title
                        handle
                        featuredImage { url altText }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        const response = await fetch("/api/2025-01/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({
            query,
            variables: { handle, variantId: variantGid },
          }),
        });

        const payload = await response.json();
        const variant = payload?.data?.product?.variant;
        const nodes = variant?.components?.nodes || [];

        this.hasNativeComponents = nodes.length > 0;

        if (variant?.id) {
          this.variantId = variant.id.split("/").pop();
          this.dataset.variantId = this.variantId;
        }

        if (!this.hasNativeComponents) {
          if (shouldRender) {
            this.clearLoading("לא נמצאו מוצרים בבאנדל");
          }
          return;
        }

        if (shouldRender) {
          this.renderComponents(nodes);
        }
      } catch (error) {
        this.hasNativeComponents = null;
        if (shouldRender) {
          this.clearLoading("לא ניתן לטעון את הבאנדל");
        }
      }
    }

    renderComponents(nodes) {
      const visibleNodes = nodes.filter((_, index) => {
        if (this.productCount === "2" && index === 0) return false;
        return true;
      });

      if (!visibleNodes.length) {
        this.clearLoading("לא נמצאו מוצרים בבאנדל");
        return;
      }

      this.productsEl.innerHTML = visibleNodes
        .map((node, visibleIndex) => {
          const sourceIndex =
            this.productCount === "2" ? visibleIndex + 1 : visibleIndex;
          const variant = node.productVariant;
          const product = variant?.product;
          const image = variant?.image || product?.featuredImage;
          const title = product?.title || "";
          const productHandle = product?.handle || "";
          const priceCents = this.toCents(
            variant?.compareAtPrice?.amount || variant?.price?.amount,
          );
          const description = this.descriptions[sourceIndex] || "";
          const plus =
            visibleIndex > 0
              ? `<span class="wd-pbundle__plus" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.5 8H15.5" stroke="#101820" stroke-linecap="round"/>
                    <path d="M8 0.5L8 15.5" stroke="#101820" stroke-linecap="round"/>
                    </svg>                  
                </span>`
              : "";

          return `
            ${plus}
            <div class="wd-pbundle__item" role="listitem" data-component-index="${sourceIndex}">
              <div class="wd-pbundle__item-card">
                ${
                  image?.url
                    ? `<a href="/products/${productHandle}" class="wd-pbundle__item-image-link">
                        <img
                          class="wd-pbundle__item-image"
                          src="${image.url}"
                          alt="${this.escapeHtml(image.altText || title)}"
                          width="100"
                          height="100"
                          loading="lazy"
                        >
                      </a>`
                    : ""
                }
              </div>
              <div class="wd-pbundle__item-meta">
                <p class="wd-pbundle__item-title">${this.escapeHtml(title)}</p>
                ${
                  priceCents
                    ? `<p class="wd-pbundle__item-price">${this.formatMoney(priceCents)}</p>`
                    : ""
                }
                ${
                  description
                    ? `<p class="wd-pbundle__item-description">${this.escapeHtml(description)}</p>`
                    : ""
                }
              </div>
            </div>
          `;
        })
        .join("");
    }

    clearLoading(message) {
      const emptyEl = this.productsEl?.querySelector(".wd-pbundle__empty");
      if (emptyEl && message) {
        emptyEl.textContent = message;
      }
    }

    toCents(amount) {
      if (amount == null || amount === "") return 0;
      return Math.round(parseFloat(amount) * 100);
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");
    }

    getSectionsParam(cart) {
      if (!cart || typeof cart.getSectionsToRender !== "function") {
        return null;
      }

      return cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(",");
    }

    handleCartAddResponse(response, cart, useDrawer, variantId) {
      if (response.status) {
        this.setError(response.description || response.message);
        this.refreshCartDrawer(cart, false);
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "wd-product-bundle",
        productVariantId: variantId,
        cartData: response,
      });

      if (useDrawer) {
        cart.openAfterAdd(response);
        return;
      }

      window.location = window.routes.cart_url;
    }

    onAdd(event) {
      event.preventDefault();

      if (this.button.disabled || this.button.classList.contains("loading")) {
        return;
      }

      if (!this.variantId) {
        this.setError("לא ניתן להוסיף את הבאנדל");
        return;
      }

      if (
        this.dataset.storefrontToken &&
        this.hasNativeComponents === false
      ) {
        this.setError(
          "הבאנדל לא מוגדר ב-Shopify Bundles. הגדירו את המוצר כבאנדל בממשק הניהול.",
        );
        return;
      }

      const cart = document.querySelector("cart-drawer");
      const useDrawer = Boolean(cart);

      this.setError();
      cart?.setActiveElement?.(document.activeElement);

      const loader = this.button.querySelector(".loading-overlay__spinner");
      this.button.classList.add("loading");
      if (loader) loader.classList.remove("hidden");

      const sections = this.getSectionsParam(cart);
      const finish = () => {
        this.button.classList.remove("loading");
        if (loader) loader.classList.add("hidden");

        if (cart?.classList?.contains("is-empty")) {
          cart.classList.remove("is-empty");
        }
      };

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData();
      formData.append("id", this.variantId);
      formData.append("quantity", 1);

      if (sections) {
        formData.append("sections", sections);
      }

      formData.append("sections_url", window.location.pathname);
      config.body = formData;

      fetch(`${window.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          this.handleCartAddResponse(
            response,
            cart,
            useDrawer,
            this.variantId,
          );
        })
        .catch(() => {
          this.setError();
        })
        .finally(finish);
    }

    refreshCartDrawer(cart, isClosedCart = true) {
      if (!cart || typeof cart.getSectionsToRender !== "function") return;

      const sectionIds = cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(",");

      fetch(`${window.Shopify.routes.root}?sections=${sectionIds}`)
        .then((response) => response.json())
        .then((sections) => {
          cart.renderContents({ sections }, isClosedCart);
        })
        .catch(() => {
          this.setError();
        });
    }

    setError(message = "") {
      if (!this.errorEl) return;

      if (message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
        return;
      }

      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }
  }

  customElements.define("wd-product-bundle", WdProductBundle);
}
